import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { ArrowLeft, DownloadCloud } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import { useLeads } from "@/features/leads/api/leads";
import { useAuthStore } from "@/store/auth";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import {
  EntityType,
  canToggleContractorActive,
  getContractorDisplayName,
  validatePeselClient,
} from "@/features/contractors/utils";
import type { ContractorFlowState } from "@/features/contractors/prefill";
import {
  isImpliedWorkType,
  normalizeWorkTypes,
  WORK_TYPE_BADGE,
  WORK_TYPES,
  type WorkType,
} from "@/features/contractors/workTypes";
import { usePageTitle } from "@/hooks/usePageTitle";

interface AddressForm {
  street: string;
  building_number: string;
  apartment_number: string;
  city: string;
  postal_code: string;
  country: string;
}

interface ContractorForm {
  entity_type: EntityType;
  name: string;
  short_name: string;
  first_name: string;
  last_name: string;
  pesel: string;
  nip: string;
  regon: string;
  krs: string;
  is_vat_payer: boolean;
  eu_vat_number: string;
  bank_account_number: string;
  bank_name: string;
  contact_person: string;
  contact_email: string;
  contact_phone: string;
  website: string;
  notes: string;
  /** Typy prowadzonych prac — checkboxy; firma może mieć kilka. Zapis normalizuje
      listę jak backend (`normalizeWorkTypes`). */
  work_types: WorkType[];
  is_active: boolean;
  /** Lead, z którego pochodzi ta firma — pole modułu Pozyskiwanie, zapisywane po
      stronie leada. `null` zdejmuje oznaczenie, `undefined` zostawia bez zmian. */
  from_lead?: string | null;
  registered_address: AddressForm;
  correspondence_address: AddressForm | null;
}

const emptyAddress = (): AddressForm => ({
  street: "", building_number: "", apartment_number: "",
  city: "", postal_code: "", country: "Polska",
});

interface RegistryPrefill {
  found: boolean;
  nip: string;
  name?: string;
  regon?: string;
  krs?: string;
  vat_status?: string;
  is_vat_payer?: boolean;
  registered_address?: AddressForm | null;
  raw_address?: string;
  bank_account_number?: string;
  account_numbers_count?: number;
}

const emptyForm = (): ContractorForm => ({
  entity_type: "company",
  name: "", short_name: "",
  first_name: "", last_name: "", pesel: "",
  nip: "", regon: "", krs: "",
  is_vat_payer: true, eu_vat_number: "",
  bank_account_number: "", bank_name: "",
  contact_person: "", contact_email: "", contact_phone: "",
  website: "", notes: "", work_types: [], is_active: true,
  registered_address: emptyAddress(),
  correspondence_address: null,
});

function Field({
  label, value, onChange, required = false, placeholder = "",
  error, type = "text",
}: {
  label: string; value: string; onChange: (v: string) => void;
  required?: boolean; placeholder?: string; error?: string; type?: string;
}) {
  return (
    <div>
      <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`block w-full rounded-md border px-3 py-2 text-sm ${error ? "border-red-400" : "border-gray-300"}`}
      />
      {error && <p className="text-xs text-red-600 mt-0.5">{error}</p>}
    </div>
  );
}

// Etykiety do podsumowania błędów pod przyciskiem zapisu. Klucze jak w API; błędy
// adresów przychodzą zagnieżdżone i po spłaszczeniu mają postać `registered_address.city`.
const ERROR_FIELD_LABELS: Record<string, string> = {
  entity_type: "Rodzaj podmiotu",
  name: "Nazwa pełna",
  short_name: "Nazwa skrócona",
  first_name: "Imię",
  last_name: "Nazwisko",
  pesel: "PESEL",
  nip: "NIP",
  regon: "REGON",
  krs: "KRS",
  is_vat_payer: "Płatnik VAT",
  eu_vat_number: "Nr VAT UE",
  bank_account_number: "Nr konta bankowego",
  bank_name: "Nazwa banku",
  contact_person: "Osoba kontaktowa",
  contact_email: "E-mail kontaktowy",
  contact_phone: "Telefon kontaktowy",
  website: "Strona WWW",
  notes: "Uwagi",
  work_types: "Typy prac",
  is_active: "Aktywny",
  from_lead: "Pochodzi z leada",
  registered_address: "Adres rejestrowy",
  correspondence_address: "Adres korespondencyjny",
};

const ADDRESS_FIELD_LABELS: Record<keyof AddressForm, string> = {
  street: "ulica",
  building_number: "nr budynku",
  apartment_number: "nr lokalu",
  city: "miejscowość",
  postal_code: "kod pocztowy",
  country: "kraj",
};

/** `null` = błąd ogólny (`detail`, `non_field_errors`) — wypisujemy sam komunikat. */
function errorFieldLabel(key: string): string | null {
  const [head, sub] = key.split(".");
  if (head === "detail" || head === "non_field_errors") return null;
  const base = ERROR_FIELD_LABELS[head] ?? head;
  if (!sub) return base;
  return `${base} — ${ADDRESS_FIELD_LABELS[sub as keyof AddressForm] ?? sub}`;
}

/**
 * Spłaszcza `errors` z koperty API do mapy pole → pierwszy komunikat. Zagnieżdżone
 * błędy adresu (`{registered_address: {city: [...]}}`) dostają klucz z kropką —
 * ten sam, którego używa walidacja po stronie przeglądarki.
 */
function flattenFieldErrors(errors: unknown, prefix = ""): Record<string, string> {
  if (errors == null || errors === "") return {};
  if (Array.isArray(errors)) {
    if (errors.length === 0) return {};
    const first = errors[0];
    return first && typeof first === "object"
      ? flattenFieldErrors(first, prefix)
      : { [prefix || "non_field_errors"]: String(first) };
  }
  if (typeof errors === "object") {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(errors as Record<string, unknown>)) {
      Object.assign(out, flattenFieldErrors(v, prefix ? `${prefix}.${k}` : k));
    }
    return out;
  }
  return { [prefix || "non_field_errors"]: String(errors) };
}

function AddressSection({
  title, addr, onChange, errors, errorPrefix,
}: {
  title: string;
  addr: AddressForm;
  onChange: (a: AddressForm) => void;
  errors: Record<string, string>;
  errorPrefix: string;
}) {
  const upd = (k: keyof AddressForm) => (v: string) => onChange({ ...addr, [k]: v });
  const err = (k: keyof AddressForm) => errors[`${errorPrefix}.${k}`];
  return (
    <div className="bg-white dark:bg-gray-800 p-5">
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">{title}</h3>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="col-span-2">
            <Field label="Ulica" value={addr.street} onChange={upd("street")} required error={err("street")} />
          </div>
          <Field label="Nr budynku" value={addr.building_number} onChange={upd("building_number")} required error={err("building_number")} />
        </div>
        <Field label="Nr lokalu" value={addr.apartment_number} onChange={upd("apartment_number")} error={err("apartment_number")} />
        <Field label="Kod pocztowy" value={addr.postal_code} onChange={upd("postal_code")} required error={err("postal_code")} />
        <Field label="Miejscowość" value={addr.city} onChange={upd("city")} required error={err("city")} />
        <Field label="Kraj" value={addr.country} onChange={upd("country")} error={err("country")} />
      </div>
    </div>
  );
}

export function ContractorFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();

  // Ścieżka „z prośby o konto do kartoteki": z czym tu przyszliśmy i co ma się stać
  // po zapisie. Trzymamy w ref, bo nawigacja po zapisie wyczyściłaby state w locie.
  const flow = useRef<ContractorFlowState>(
    (location.state as ContractorFlowState | null) ?? {},
  ).current;

  const [form, setForm] = useState<ContractorForm>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const formInitialized = useRef(false);


  const { data: contractorData, isLoading } = useQuery({
    queryKey: ["contractors", id],
    queryFn: () =>
      apiClient
        .get<{ data: (ContractorForm & { id: string })[] }>("/contractors/", { params: { contractor: id } })
        .then((r) => r.data.data[0] ?? null),
    enabled: isEdit,
  });
  usePageTitle(isEdit ? (contractorData && `Edycja: ${getContractorDisplayName(contractorData)}`) : null);

  // Pole „pochodzi z leada" widzi tylko ten, kto ma dostęp do Pozyskiwania —
  // dla reszty firmy lista leadów to dane, których nie powinni oglądać, a samo
  // pole i tak nie miałoby dla nich znaczenia.
  const canSeeLeads = !!useAuthStore((s) => s.user?.has_lead_access);
  // Aktywny/nieaktywny przełącza tylko CEO albo dyrektor techniczny — reszta z
  // uprawnieniem do edycji podwykonawców zmienia dane, ale nie status (backend: 403).
  const canToggleActive = canToggleContractorActive(useAuthStore((s) => s.user));
  const { data: leads } = useLeads(canSeeLeads ? { include_blocked: true } : {});
  const currentLeadId =
    (contractorData as { from_lead_id?: string | null } | null)?.from_lead_id ?? null;
  // Lista do wyboru: firmy jeszcze nieprzypisane plus ta już wskazana, żeby przy
  // edycji nie zniknęła z własnego pola.
  const leadOptions = (leads ?? []).filter(
    (lead) => !lead.contractor || lead.id === currentLeadId,
  );

  // onSuccess was removed in TanStack Query v5 — populate form once data arrives
  useEffect(() => {
    if (!contractorData || formInitialized.current) return;
    formInitialized.current = true;
    setForm({
      entity_type: (contractorData.entity_type as EntityType) ?? "company",
      name: contractorData.name ?? "",
      short_name: contractorData.short_name ?? "",
      first_name: contractorData.first_name ?? "",
      last_name: contractorData.last_name ?? "",
      pesel: contractorData.pesel ?? "",
      nip: contractorData.nip ?? "",
      regon: contractorData.regon ?? "",
      krs: contractorData.krs ?? "",
      is_vat_payer: contractorData.is_vat_payer ?? false,
      eu_vat_number: contractorData.eu_vat_number ?? "",
      bank_account_number: contractorData.bank_account_number ?? "",
      bank_name: contractorData.bank_name ?? "",
      contact_person: contractorData.contact_person ?? "",
      contact_email: contractorData.contact_email ?? "",
      contact_phone: contractorData.contact_phone ?? "",
      website: contractorData.website ?? "",
      notes: contractorData.notes ?? "",
      work_types: normalizeWorkTypes(contractorData.work_types ?? []),
      is_active: contractorData.is_active ?? true,
      // Bez klucza w formularzu zapis nie rusza powiązania — zmieniamy je dopiero,
      // gdy ktoś świadomie wybierze (albo wyczyści) lead w polu poniżej.
      registered_address: contractorData.registered_address ?? emptyAddress(),
      correspondence_address: contractorData.correspondence_address ?? null,
    });
  }, [contractorData]);

  const saveMutation = useMutation({
    mutationFn: (payload: ContractorForm) =>
      isEdit
        ? apiClient.patch(`/contractors/${id}/`, payload).then((r) => r.data.data)
        : apiClient.post("/contractors/", payload).then((r) => r.data.data),
    onSuccess: async (data: { id: string }) => {
      qc.invalidateQueries({ queryKey: ["contractors"] });
      const contractorId = data.id ?? id;
      if (!isEdit && flow.accountRequestId && contractorId) {
        // Ślad „to zgłoszenie skończyło się tą firmą". Nieudany zapis śladu nie może
        // zatrzymać człowieka w drodze po konto — firma już jest w kartotece.
        try {
          await apiClient.post(
            `/rfq/account-requests/${flow.accountRequestId}/link-contractor/`,
            { contractor: contractorId },
          );
          qc.invalidateQueries({ queryKey: ["rfq", "account-requests"] });
        } catch {
          /* pusto celowo — patrz komentarz wyżej */
        }
      }
      navigate(
        `/contractors/${contractorId}`,
        !isEdit && flow.openUserForm
          ? {
              state: {
                openUserForm: flow.openUserForm,
                accountRequestId: flow.accountRequestId,
              } satisfies ContractorFlowState,
            }
          : undefined,
      );
    },
    onError: (err: { response?: { data?: { errors?: unknown } } }) => {
      setErrors(flattenFieldErrors(err.response?.data?.errors));
    },
  });

  const upd = (k: keyof ContractorForm) => (v: string | boolean) =>
    setForm((prev) => ({ ...prev, [k]: v }));
  // Zaznaczenie „Geodeta z WPW" dokłada „Geodetę" (ten sam wynik, co normalizacja na
  // backendzie) — żeby formularz nie obiecywał kombinacji, której zapis i tak poprawi.
  const toggleWorkType = (kind: WorkType, checked: boolean) =>
    setForm((prev) => ({
      ...prev,
      work_types: normalizeWorkTypes(
        checked ? [...prev.work_types, kind] : prev.work_types.filter((k) => k !== kind),
      ),
    }));

  const [lookupPending, setLookupPending] = useState(false);
  const [lookupNote, setLookupNote] = useState<{ tone: "ok" | "warn" | "error"; text: string } | null>(null);

  const handleRegistryLookup = async (nipOverride?: string) => {
    const nip = (nipOverride ?? form.nip).trim();
    setLookupPending(true);
    setLookupNote(null);
    try {
      const { data } = await apiClient
        .get<{ data: RegistryPrefill }>("/contractors/registry-lookup/", { params: { nip } })
        .then((r) => r.data);
      if (!data.found) {
        setLookupNote({ tone: "warn", text: "NIP poprawny, ale podmiotu nie ma na białej liście VAT. Uzupełnij dane ręcznie." });
        return;
      }
      setForm((prev) => {
        // Create: fill everything. Edit: fill only empty fields — never silently
        // overwrite hand-entered data.
        const fill = (current: string, fetched?: string) =>
          (isEdit && current.trim() ? current : fetched ?? current);
        const addrEmpty =
          !prev.registered_address.street.trim() &&
          !prev.registered_address.city.trim() &&
          !prev.registered_address.postal_code.trim();
        return {
          ...prev,
          name: fill(prev.name, data.name),
          nip: data.nip,
          regon: fill(prev.regon, data.regon),
          krs: fill(prev.krs, data.krs),
          is_vat_payer: isEdit ? prev.is_vat_payer : data.is_vat_payer ?? prev.is_vat_payer,
          bank_account_number: fill(prev.bank_account_number, data.bank_account_number),
          registered_address:
            addrEmpty && data.registered_address ? data.registered_address : prev.registered_address,
        };
      });
      const notes: string[] = [`Pobrano z białej listy VAT: ${data.name} (status VAT: ${data.vat_status || "?"}).`];
      if ((data.account_numbers_count ?? 0) > 1) {
        notes.push(`Podmiot ma ${data.account_numbers_count} rachunków na białej liście — wpisz ten z umowy/faktury.`);
      }
      if (!data.registered_address && data.raw_address) {
        notes.push(`Nie udało się rozbić adresu — przepisz ręcznie: ${data.raw_address}`);
      }
      setLookupNote({ tone: "ok", text: notes.join(" ") });
    } catch (err) {
      const e = err as { response?: { status?: number; data?: { errors?: { nip?: string[] } } } };
      if (e.response?.status === 400) {
        setLookupNote({ tone: "error", text: e.response.data?.errors?.nip?.[0] ?? "Niepoprawny NIP." });
      } else if (e.response?.status === 503) {
        setLookupNote({ tone: "error", text: "Rejestr (biała lista MF) nie odpowiada. Spróbuj ponownie później." });
      } else {
        setLookupNote({ tone: "error", text: "Nie udało się pobrać danych z rejestrów." });
      }
    } finally {
      setLookupPending(false);
    }
  };

  // Dane z prośby o konto wchodzą do pustego formularza raz, a resztę (adres, REGON,
  // KRS, konto) dociągamy z rejestrów po NIP — człowiek ma poprawiać, nie przepisywać.
  const prefillApplied = useRef(false);
  useEffect(() => {
    const prefill = flow.contractorPrefill;
    if (isEdit || prefillApplied.current || !prefill) return;
    prefillApplied.current = true;
    setForm((prev) => ({
      ...prev,
      name: prefill.name ?? "",
      nip: prefill.nip ?? "",
      contact_person: prefill.contact_person ?? "",
      contact_email: prefill.contact_email ?? "",
      contact_phone: prefill.contact_phone ?? "",
    }));
    if ((prefill.nip ?? "").trim()) void handleRegistryLookup(prefill.nip);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, flow]);

  const validate = () => {
    const errs: Record<string, string> = {};
    if (form.entity_type === "individual") {
      if (!form.first_name.trim()) errs.first_name = "Imię jest wymagane";
      if (!form.last_name.trim()) errs.last_name = "Nazwisko jest wymagane";
      if (!form.pesel.trim()) errs.pesel = "PESEL jest wymagany";
      else if (!validatePeselClient(form.pesel.trim())) errs.pesel = "Niepoprawny PESEL (11 cyfr, sprawdź sumę kontrolną)";
    } else {
      if (!form.name.trim()) errs.name = "Nazwa jest wymagana";
      if (!form.nip.trim()) errs.nip = "NIP jest wymagany";
    }
    if (!form.contact_email.trim()) errs.contact_email = "E-mail kontaktowy jest wymagany";
    if (!form.registered_address.street.trim()) errs["registered_address.street"] = "Ulica jest wymagana";
    if (!form.registered_address.building_number.trim()) errs["registered_address.building_number"] = "Nr budynku jest wymagany";
    if (!form.registered_address.city.trim()) errs["registered_address.city"] = "Miejscowość jest wymagana";
    if (!form.registered_address.postal_code.trim()) errs["registered_address.postal_code"] = "Kod pocztowy jest wymagany";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const buildPayload = () => {
    if (form.entity_type === "individual") {
      // Wyzeruj pola firmowe — backend nie egzekwuje ich dla osoby, ale czystość payload-u ułatwia debug.
      return { ...form, name: "", nip: "", regon: "", krs: "", eu_vat_number: "", is_vat_payer: false };
    }
    // Company: wyzeruj pola osoby.
    return { ...form, first_name: "", last_name: "", pesel: "" };
  };

  const handleSubmit = () => {
    if (!validate()) return;
    saveMutation.mutate(buildPayload() as ContractorForm);
  };

  if (isEdit && isLoading) return <PageLoader />;

  return (
    <div className="max-w-3xl">
      <Link
        to={isEdit ? `/contractors/${id}` : "/contractors"}
        className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 mb-5"
      >
        <ArrowLeft size={14} /> {isEdit ? "Podwykonawca" : "Podwykonawcy"}
      </Link>

      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
        {isEdit ? "Edytuj podwykonawcę" : "Nowy podwykonawca"}
      </h1>

      <div className="flex flex-col gap-5">
        {/* Basic info */}
        <div className="bg-white dark:bg-gray-800 p-5">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Dane podstawowe</h2>

          {/* Entity-type toggle */}
          <div className="flex gap-2 mb-4">
            {(["company", "individual"] as EntityType[]).map((et) => {
              const active = form.entity_type === et;
              return (
                <button
                  key={et}
                  type="button"
                  onClick={() => upd("entity_type")(et)}
                  className={`px-4 py-2 text-sm rounded-md border transition ${
                    active
                      ? "bg-[var(--color-primary)] text-white border-[var(--color-primary)]"
                      : "bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {et === "company" ? "Firma" : "Osoba fizyczna (UZ/UoD)"}
                </button>
              );
            })}
          </div>

          {form.entity_type === "individual" ? (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Imię" value={form.first_name} onChange={upd("first_name") as (v: string) => void} required error={errors.first_name} />
              <Field label="Nazwisko" value={form.last_name} onChange={upd("last_name") as (v: string) => void} required error={errors.last_name} />
              <Field label="PESEL" value={form.pesel} onChange={upd("pesel") as (v: string) => void} required error={errors.pesel} placeholder="11 cyfr" />
              <Field label="Nazwa skrócona (opcjonalnie)" value={form.short_name} onChange={upd("short_name") as (v: string) => void} error={errors.short_name} />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Field label="Nazwa pełna" value={form.name} onChange={upd("name") as (v: string) => void} required error={errors.name} />
              </div>
              <Field label="Nazwa skrócona" value={form.short_name} onChange={upd("short_name") as (v: string) => void} error={errors.short_name} />
              <div>
                <Field label="NIP" value={form.nip} onChange={upd("nip") as (v: string) => void} required error={errors.nip} />
                <button
                  type="button"
                  onClick={() => handleRegistryLookup()}
                  disabled={lookupPending || !form.nip.trim()}
                  className="mt-1.5 inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline disabled:text-gray-400 disabled:no-underline"
                >
                  <DownloadCloud size={13} />
                  {lookupPending ? "Pobieranie…" : "Pobierz z rejestrów (biała lista + KRS)"}
                </button>
              </div>
              <Field label="REGON" value={form.regon} onChange={upd("regon") as (v: string) => void} error={errors.regon} />
              <Field label="KRS" value={form.krs} onChange={upd("krs") as (v: string) => void} error={errors.krs} />
              <Field label="Nr VAT UE" value={form.eu_vat_number} onChange={upd("eu_vat_number") as (v: string) => void} error={errors.eu_vat_number} />
              {lookupNote && (
                <p
                  className={`col-span-2 text-xs ${
                    lookupNote.tone === "ok"
                      ? "text-green-700 dark:text-green-400"
                      : lookupNote.tone === "warn"
                        ? "text-amber-700 dark:text-amber-400"
                        : "text-red-600 dark:text-red-400"
                  }`}
                >
                  {lookupNote.text}
                </p>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-6 pt-4">
            {form.entity_type === "company" && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_vat_payer}
                  onChange={(e) => upd("is_vat_payer")(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Płatnik VAT</span>
              </label>
            )}
            {canToggleActive && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => upd("is_active")(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Aktywny</span>
              </label>
            )}
          </div>
        </div>

        {/* Typy prowadzonych prac — własna sekcja, celowo osobno od „Płatnik VAT" i „Aktywny"
            (to nie są przełączniki kartoteki, tylko role firmy): od nich zależą kręgi przy
            zlecaniu (WPW, MDCP, projektant sprawdzający) i grupy na megamapie podwykonawców. */}
        <fieldset className="bg-white dark:bg-gray-800 p-5">
          <legend className="float-left w-full text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
            Typy prowadzonych prac
          </legend>
          <p className="clear-both text-xs text-gray-500 dark:text-gray-400 mb-3">
            Można zaznaczyć kilka. Firma bez typu to zwykły podwykonawca — nie wchodzi do
            kręgów podpowiedzi ani na megamapę.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {WORK_TYPES.map((t) => {
              const implied = isImpliedWorkType(t.value, form.work_types);
              const checked = form.work_types.includes(t.value);
              return (
                <label
                  key={t.value}
                  className={`flex items-start gap-2.5 rounded-md border px-3 py-2 transition ${
                    checked
                      ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)]"
                      : "border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                  } ${implied ? "cursor-default" : "cursor-pointer"}`}
                  title={implied ? `Wynika z „Geodeta z WPW” — ${t.hint}` : t.hint}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={implied}
                    onChange={(e) => toggleWorkType(t.value, e.target.checked)}
                    className="rounded mt-0.5"
                    aria-label={t.label}
                  />
                  <span className="min-w-0 text-sm text-gray-700 dark:text-gray-300">
                    <span className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-medium ${WORK_TYPE_BADGE[t.value]}`}>
                      {t.label}
                    </span>
                    <span className="block mt-0.5 text-xs text-gray-400 dark:text-gray-500">{t.hint}</span>
                  </span>
                </label>
              );
            })}
          </div>
          {errors.work_types && <p className="text-xs text-red-600 mt-1">{errors.work_types}</p>}
        </fieldset>

        {/* Addresses */}
        <AddressSection
          title="Adres rejestrowy"
          addr={form.registered_address}
          onChange={(a) => setForm((prev) => ({ ...prev, registered_address: a }))}
          errors={errors}
          errorPrefix="registered_address"
        />

        <div className="bg-white dark:bg-gray-800 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Adres korespondencyjny</h3>
            <button
              type="button"
              className="text-xs text-[var(--color-primary)] hover:underline"
              onClick={() =>
                setForm((prev) => ({
                  ...prev,
                  correspondence_address: prev.correspondence_address ? null : emptyAddress(),
                }))
              }
            >
              {form.correspondence_address ? "Usuń" : "Dodaj"}
            </button>
          </div>
          {form.correspondence_address && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Field
                    label="Ulica"
                    value={form.correspondence_address.street}
                    error={errors["correspondence_address.street"]}
                    onChange={(v) =>
                      setForm((prev) => ({
                        ...prev,
                        correspondence_address: { ...prev.correspondence_address!, street: v },
                      }))
                    }
                  />
                </div>
                <Field
                  label="Nr budynku"
                  value={form.correspondence_address.building_number}
                  error={errors["correspondence_address.building_number"]}
                  onChange={(v) =>
                    setForm((prev) => ({
                      ...prev,
                      correspondence_address: { ...prev.correspondence_address!, building_number: v },
                    }))
                  }
                />
              </div>
              <Field
                label="Nr lokalu"
                value={form.correspondence_address.apartment_number}
                error={errors["correspondence_address.apartment_number"]}
                onChange={(v) =>
                  setForm((prev) => ({
                    ...prev,
                    correspondence_address: { ...prev.correspondence_address!, apartment_number: v },
                  }))
                }
              />
              <Field
                label="Kod pocztowy"
                value={form.correspondence_address.postal_code}
                error={errors["correspondence_address.postal_code"]}
                onChange={(v) =>
                  setForm((prev) => ({
                    ...prev,
                    correspondence_address: { ...prev.correspondence_address!, postal_code: v },
                  }))
                }
              />
              <Field
                label="Miejscowość"
                value={form.correspondence_address.city}
                error={errors["correspondence_address.city"]}
                onChange={(v) =>
                  setForm((prev) => ({
                    ...prev,
                    correspondence_address: { ...prev.correspondence_address!, city: v },
                  }))
                }
              />
              <Field
                label="Kraj"
                value={form.correspondence_address.country}
                error={errors["correspondence_address.country"]}
                onChange={(v) =>
                  setForm((prev) => ({
                    ...prev,
                    correspondence_address: { ...prev.correspondence_address!, country: v },
                  }))
                }
              />
            </div>
          )}
          {!form.correspondence_address && (
            <p className="text-sm text-gray-400 dark:text-gray-500">Brak — używany jest adres rejestrowy.</p>
          )}
        </div>

        {/* Banking & contact */}
        <div className="bg-white dark:bg-gray-800 p-5">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Dane bankowe i kontaktowe</h2>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Field label="Nr konta bankowego" value={form.bank_account_number} onChange={upd("bank_account_number") as (v: string) => void} error={errors.bank_account_number} />
            </div>
            <Field label="Nazwa banku" value={form.bank_name} onChange={upd("bank_name") as (v: string) => void} error={errors.bank_name} />
            <Field label="Osoba kontaktowa" value={form.contact_person} onChange={upd("contact_person") as (v: string) => void} error={errors.contact_person} />
            <Field label="E-mail kontaktowy" value={form.contact_email} onChange={upd("contact_email") as (v: string) => void} type="email" required error={errors.contact_email} />
            <Field label="Telefon kontaktowy" value={form.contact_phone} onChange={upd("contact_phone") as (v: string) => void} error={errors.contact_phone} />
            <div className="col-span-2">
              <Field label="Strona WWW" value={form.website} onChange={upd("website") as (v: string) => void} error={errors.website} />
            </div>
            {canSeeLeads && (
              <div className="col-span-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Pochodzi z leada
                </label>
                <select
                  value={form.from_lead ?? currentLeadId ?? ""}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, from_lead: e.target.value || null }))
                  }
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">— nie z pozyskiwania —</option>
                  {leadOptions.map((lead) => (
                    <option key={lead.id} value={lead.id}>
                      {lead.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Spina firmę z kartą leada: lead dostaje status „Wszedł do systemu”,
                  a w lejku widać, które źródło pozyskania ją przyniosło.
                </p>
              </div>
            )}

            <div className="col-span-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Uwagi</label>
              <textarea
                value={form.notes}
                onChange={(e) => upd("notes")(e.target.value)}
                rows={3}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
          </div>
        </div>

        {/* Przycisk jest na dole, a odrzucone pole bywa na górze strony (REGON, adres)
            — bez tej listy odmowa zapisu wyglądała jak przycisk, który nic nie robi. */}
        {Object.keys(errors).length > 0 && (
          <div
            role="alert"
            className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 px-4 py-3 text-sm text-red-700 dark:text-red-300"
          >
            <p className="font-medium">Nie zapisano. Popraw dane:</p>
            <ul className="mt-1 list-disc pl-5">
              {Object.entries(errors).map(([key, message]) => {
                const label = errorFieldLabel(key);
                return (
                  <li key={key}>
                    {label && <span className="font-medium">{label}: </span>}
                    {message}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {saveMutation.isError && Object.keys(errors).length === 0 && (
          <p className="text-sm text-red-600">Błąd zapisu. Sprawdź dane i spróbuj ponownie.</p>
        )}

        <div className="flex gap-3 pb-8">
          <Button loading={saveMutation.isPending} onClick={handleSubmit}>
            {isEdit ? "Zapisz zmiany" : "Utwórz podwykonawcę"}
          </Button>
          <Button variant="secondary" onClick={() => navigate(isEdit ? `/contractors/${id}` : "/contractors")}>
            Anuluj
          </Button>
        </div>
      </div>
    </div>
  );
}
